import './services/firebaseApp'

import { StyleProvider, I18NProvider, useEffect, TypeGuards } from '@codeleap/common'
import { Settings, variantProvider, React, I18N, LocalStorageKeys } from './app'
import {
  AppStatusOverlay,
  DebugButton,
  GlobalStyle,
  NoReviewQuestionsModal,
  OrganisationInfo,
  ReviewOrganisation,
  ReviewOrgSuccessModal,
  UnknownOrganisation,
  CongratulationsStories,
  variants,
  AchievementDetail,
  AchievementModal,
  PlayerYoutube,
  OnboardingQuestions,
  SignInNewsFeed,
  HereWeGoModal,
  ImpersonatingBanner,
  AchievementToast,
} from './components'
import { Provider } from 'react-redux'
import { AppStatus, Pulse, store, useAppSelector } from './redux'
import { APIClient, analytics, amplitude } from './services'
import CreateStory from './components/Modals/CreateStory'
import StoryDetails from './components/Modals/StoryDetail'
import { Location, useLocation } from '@reach/router'
import { toggleOnboardingQuestionsVisibility, LocalStorage, useHandleOpen, useUtm } from './utils'
import { globalHistory } from '@reach/router'
import { ModalOutlet } from '@codeleap/modals'
import { ImmersiveVideo } from './components/Player/Immersive'
import { QueryClientProvider } from '@tanstack/react-query'

amplitude.sessionReplayManager.initialize()

export const Overlays = () => (
  // This is needed for useLocation to work inside the modals
  <Location>
    {() => <>
      <DebugButton />
      <OrganisationInfo />
      <ReviewOrganisation />
      <UnknownOrganisation />
      <CreateStory />
      <NoReviewQuestionsModal />
      <ReviewOrgSuccessModal />
      <StoryDetails />
      <OnboardingQuestions />
      <SignInNewsFeed />
      <CongratulationsStories />
      <AchievementDetail />
      <AchievementModal />
      <PlayerYoutube.Fullscreen />
      <HereWeGoModal />
      <AppStatusOverlay />
      <ModalOutlet />
      <ImpersonatingBanner />
    </>
    }
  </Location>
)

const App = ({ children }) => {
  const response = APIClient.Session.useSession(true)
  useHandleOpen({
    mapping: {
      openQuestions: undefined,
      questions: undefined,
      openCreateStory: 'createStory',
    },
    onOpen: () => Pulse.toggleModal('start_page', true),
  })

  const { onboardingQuestionsWasAppeared } = useAppSelector((s) => s.Session)
  const { pathname } = useLocation()

  useEffect(() => {
    const storage = JSON.parse(LocalStorage.getItem(LocalStorageKeys.ONBOARDING_QUESTIONS))
    const profileAnswers = response.profile?.climate_actionability && response.profile?.climate_priority
    const routesToAvoid = ['/profile/settings/']

    if (
      TypeGuards.isNull(storage) &&
      !profileAnswers &&
      response.loginResolved &&
      !onboardingQuestionsWasAppeared &&
      !routesToAvoid.includes(pathname)
    ) {
      AppStatus.setModal(['onboardingQuestions', true])
      toggleOnboardingQuestionsVisibility()
    }
  }, [response?.profile, response.loginResolved, pathname])

  //@ts-ignore
  useUtm(response.profile)

  useEffect(() => {
    globalHistory.listen(({ action }) => {
      if (response.isLoggedIn && action === 'PUSH') {
        analytics?.track('logged_in_pageview')
      }
    })
  }, [globalHistory])

  ImmersiveVideo.useCaptureFirstInteraction()

  return (
    <>
      <GlobalStyle />
      {children}

      <Overlays />
    </>
  )
}

export const Root = ({ children }) => {
  return (
    <QueryClientProvider client={APIClient.queryClient.client}>
      <Provider store={store}>
        <StyleProvider
          settings={Settings}
          variantProvider={variantProvider}
          variants={variants}
          logger={logger}
        >
          <AchievementToast />
          
          <I18NProvider i18n={I18N}>
            <Location>
              {() => <>
                <App>
                  {children}
                </App>

              </>}
            </Location>
          </I18NProvider>
        </StyleProvider>
      </Provider>
    </QueryClientProvider>
  )
}
